import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../Redux/Store/index.ts";
import { Button, Container } from "react-bootstrap";
import SideBar from "../Components/SideBar.tsx";
import Header from "../Components/Header.tsx";
import "./Home.css";
import FiltroSidenav from "../Components/FiltroSidenav.tsx";
import { ContrattiColumns } from "../lib/ColonneTabelle.ts";
import Tabelle from "../Components/Table.tsx";
import { useNavigate } from "react-router-dom";
import { Committenti, Contratti } from "../lib/interface.ts";

const ContrattiPage: React.FC = () => {
  const contrattiOriginal = useSelector((state: RootState) => state.contratti.contratti);
  const committenti = useSelector((state: RootState) => state.committenti.committenti);

  const [contrattiTable, setContrattiTable] = useState<Contratti[]>([]);
  const [contrattiFiltrati, setContrattiFiltrati] = useState<Contratti[]>([]);
  const [archived, setArchived] = useState(false);
  const [showFiltro, setShowFiltro] = useState(false);

  const navigate = useNavigate();
  const today = new Date();

  // 1) Funzione che filtra i contratti basandosi sulle date ISO originali
  const filterContratti = (list: Contratti[], archived: boolean) => {
    return list.filter((contratto) => {
      const dataFineParsed = contratto.dataFine ? new Date(contratto.dataFine) : today; // se manca dataFine, lo consideriamo "odierno"
      // Se archiviamo => dataFine < oggi, altrimenti dataFine >= oggi
      return archived ? dataFineParsed < today : dataFineParsed >= today;
    });
  };

  // 2) Converte una data ISO ("2025-01-31") in formato "31/01/2025"
  const formatDateIT = (isoDate?: string): string => {
    if (!isoDate) return "";
    const [yyyy, mm, dd] = isoDate.split("-");
    if (!yyyy || !mm || !dd) return isoDate; // fallback
    return `${dd}/${mm}/${yyyy}`;
  };

  // 3) Crea nuovi campi "visuali" dataInizioDisplay/dataFineDisplay senza toccare i campi originali
  const preparaContrattiForTable = (list: Contratti[], comms: Committenti[]): Contratti[] => {
    return list.map((contratto) => {
      const primoReferente = contratto.referenti?.[0];
      return {
        ...contratto,
        // Campi SOLO per la vista
        dataInizioDisplay: formatDateIT(contratto.dataInizio),
        dataFineDisplay: formatDateIT(contratto.dataFine),

        nomeCommittente: comms.find((c) => c.id === contratto.committenteId)?.ragioneSociale || "N/A",
        nomeReferente: primoReferente
          ? `${primoReferente.nome || ""} ${primoReferente.cognome || ""}`.trim()
          : "Nessun referente",
        importo: new Intl.NumberFormat("it-IT", {
          style: "currency",
          currency: "EUR",
          minimumFractionDigits: 2,
        }).format(contratto.importo || 0),
      };
    });
  };

  // Al mount e a ogni variazione di contrattiOriginal/archived, filtra e formatta
  useEffect(() => {
    const contrattiFiltrati = filterContratti(contrattiOriginal, archived);
    const contrattiVisualizzati = preparaContrattiForTable(contrattiFiltrati, committenti);
    setContrattiTable(contrattiVisualizzati);
  }, [contrattiOriginal, committenti, archived]);

  // Se l'utente usa FiltroSidenav, aggiorniamo la tabella con i filtri scelti
  useEffect(() => {
    if (contrattiFiltrati.length > 0) {
      const contrattiFiltratiUlteriormente = filterContratti(contrattiFiltrati, archived);
      setContrattiTable(preparaContrattiForTable(contrattiFiltratiUlteriormente, committenti));
    }
  }, [contrattiFiltrati, archived, committenti]);

  const handleArchivedClick = () => setArchived((prev) => !prev);
  const handleFilterClick = () => setShowFiltro(true);
  const handleAddClick = () => navigate("/crea/contratti");

  return (
    <Container fluid className="d-flex justify-content-between" style={{ margin: 0, padding: 0 }}>
      <Container fluid style={{ margin: 0, padding: 0, width: "5%" }}>
        <SideBar />
      </Container>

      <Container fluid className="colonna">
        {committenti.length > 0 && (
          <div className="d-flex justify-content-center">
            <Header
              title={archived ? "Contratti Archiviati" : "Contratti in Essere"}
              buttons={[
                {
                  label: archived ? "In essere" : "Archiviati",
                  onClick: handleArchivedClick,
                },
                { label: "Filtra", onClick: handleFilterClick },
                {
                  label: "Aggiungi un nuovo contratto",
                  onClick: handleAddClick,
                },
              ]}
            />
          </div>
        )}

        <div className="d-flex justify-content-center">
          {committenti.length > 0 ? (
            <div className="table-card">
              {/*
                IMPORTANTE: nelle tue ContrattiColumns, 
                assicurati di usare "row.dataInizioDisplay" e "row.dataFineDisplay"
                se vuoi mostrare "gg/MM/yyyy".
              */}
              <Tabelle data={contrattiTable} columns={ContrattiColumns} type="contratti" tableTitle="Contratti" />
            </div>
          ) : (
            <div className="d-flex flex-column justify-content-center align-items-center" style={{ height: "80vh" }}>
              <h2 className="text-center">Per creare un Contratto devi prima aver creato un Cliente</h2>
              <Button className="button mt-3" onClick={() => navigate("/committenti")}>
                Vai a Clienti
              </Button>
            </div>
          )}
        </div>
      </Container>

      {/* Filtro a scomparsa */}
      <FiltroSidenav
        visible={showFiltro}
        onHide={() => setShowFiltro(false)}
        setTable={(c) => setContrattiFiltrati(c)}
      />
    </Container>
  );
};

export default ContrattiPage;
